/**
 * FLAGR API client for feature flag evaluation
 * @module lib/feature-flags/flagrApi
 */

const JOBS_PGA_ORG_TAG = "jobs-pga-org";

/**
 * Fetches all active flag keys for this application from FLAGR
 * @returns {Promise<Set<string>>} Set of active flag keys
 */
const fetchActiveFlagKeys = async () => {
  const rootEndpoint =
    process.env.REACT_APP_FLAGR_API_ROOT || "https://flagr.dev.pga.com/api/v1";

  const res = await fetch(
    `${rootEndpoint}/flags?enabled=true&tags=${JOBS_PGA_ORG_TAG}`,
  );

  if (!res.ok) {
    throw new Error(`Failed to fetch active flags: ${res.status}`);
  }

  const data = await res.json();
  return new Set(data.map((flag) => flag.key));
};

/**
 * Evaluates flags for a given user
 * @param {string[]} flagKeys - Array of flag keys to evaluate
 * @param {Object} user - User object with identification info
 * @param {string} [user.universalId] - User's universal ID
 * @param {string} [user.primaryEmail] - User's email address
 * @returns {Promise<Object>} Object mapping flag keys to variant keys
 */
const evaluateFlags = async (flagKeys, user) => {
  const rootEndpoint =
    process.env.REACT_APP_FLAGR_API_ROOT || "https://flagr.dev.pga.com/api/v1";

  const res = await fetch(`${rootEndpoint}/evaluation/batch`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      enableDebug: false,
      flagKeys,
      entities: [
        {
          entityID: (user && user.universalId) || "",
          entityType: "user",
          entityContext:
            user != null
              ? { user_id: user.universalId, email: user.primaryEmail }
              : {},
        },
      ],
    }),
  });

  if (!res.ok) {
    throw new Error(`Failed to evaluate flags: ${res.status}`);
  }

  const data = await res.json();

  const flagEvaluations = data.evaluationResults.reduce((acc, val) => {
    const { flagKey, variantKey } = val;
    acc[flagKey] = variantKey;
    return acc;
  }, {});

  return flagEvaluations;
};

export { fetchActiveFlagKeys, evaluateFlags };
